import React, { useState } from "react"
import { CollapsableFilter } from "./collapsable_filter"

const t = (key: string) => window.i18n.t(key)

interface FundingsFilterProps {
  seriesValues: string[]
  amountFrom?: number
  amountTo?: number
  dateValue?: string
  dateFrom?: number
  dateTo?: number
  onChangeSeriesValues: (values: string[]) => void
  onChangeAmountFrom: (value: number | undefined) => void
  onChangeAmountTo: (value: number | undefined) => void
  onChangeDateValue: (value: string | undefined) => void
  onChangeDateFrom: (value: number | undefined) => void
  onChangeDateTo: (value: number | undefined) => void
  isLocked?: boolean
}

// Series values with their translation keys (value differs from key only for "series_c+")
const VALID_SERIES = [
  { value: "pre_seed", key: "pre_seed" },
  { value: "seed", key: "seed" },
  { value: "pre_series_a", key: "pre_series_a" },
  { value: "series_a", key: "series_a" },
  { value: "pre_series_b", key: "pre_series_b" },
  { value: "series_b", key: "series_b" },
  { value: "pre_series_c", key: "pre_series_c" },
  { value: "series_c+", key: "series_c_plus" },
  { value: "other", key: "other" },
]

const DATE_OPTIONS = ["7d", "1m", "3m", "6m", "1y", "custom"]

// Utility functions moved to module scope to avoid recreation on each render
function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

function parseNumber(str: string): number | undefined {
  const cleaned = str.replace(/,/g, "")
  const parsed = parseInt(cleaned, 10)
  return isNaN(parsed) ? undefined : parsed
}

export function FundingsFilter({
  seriesValues = [],
  amountFrom,
  amountTo,
  dateValue,
  dateFrom,
  dateTo,
  onChangeSeriesValues,
  onChangeAmountFrom,
  onChangeAmountTo,
  onChangeDateValue,
  onChangeDateFrom,
  onChangeDateTo,
  isLocked = false,
}: FundingsFilterProps) {
  const [amountFromDisplay, setAmountFromDisplay] = useState(
    amountFrom ? formatNumber(amountFrom) : ""
  )
  const [amountToDisplay, setAmountToDisplay] = useState(amountTo ? formatNumber(amountTo) : "")

  const hasValues =
    seriesValues.length > 0 ||
    amountFrom !== undefined ||
    amountTo !== undefined ||
    dateValue !== undefined ||
    dateFrom !== undefined ||
    dateTo !== undefined

  // Calculate actual count of active filter groups
  const activeFilterCount =
    seriesValues.length +
    (amountFrom !== undefined || amountTo !== undefined ? 1 : 0) +
    (dateValue !== undefined || dateFrom !== undefined || dateTo !== undefined ? 1 : 0)

  const allSeriesSelected = seriesValues.length === VALID_SERIES.length

  const handleToggleSeries = (seriesValue: string) => {
    if (seriesValues.includes(seriesValue)) {
      onChangeSeriesValues(seriesValues.filter((s) => s !== seriesValue))
    } else {
      onChangeSeriesValues([...seriesValues, seriesValue])
    }
  }

  const handleToggleAllSeries = () => {
    if (allSeriesSelected) {
      onChangeSeriesValues([])
    } else {
      onChangeSeriesValues(VALID_SERIES.map((s) => s.value))
    }
  }

  const handleAmountFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAmountFromDisplay(value)
    onChangeAmountFrom(parseNumber(value))
  }

  const handleAmountFromBlur = () => {
    const parsed = parseNumber(amountFromDisplay)
    if (parsed !== undefined) {
      setAmountFromDisplay(formatNumber(parsed))
    }
  }

  const handleAmountToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAmountToDisplay(value)
    onChangeAmountTo(parseNumber(value))
  }

  const handleAmountToBlur = () => {
    const parsed = parseNumber(amountToDisplay)
    if (parsed !== undefined) {
      setAmountToDisplay(formatNumber(parsed))
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === "") {
      onChangeDateValue(undefined)
      onChangeDateFrom(undefined)
      onChangeDateTo(undefined)
    } else {
      onChangeDateValue(value)
      if (value !== "custom") {
        onChangeDateFrom(undefined)
        onChangeDateTo(undefined)
      }
    }
  }

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChangeDateFrom(value ? parseInt(value, 10) : undefined)
  }

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChangeDateTo(value ? parseInt(value, 10) : undefined)
  }

  const handleClear = () => {
    onChangeSeriesValues([])
    onChangeAmountFrom(undefined)
    onChangeAmountTo(undefined)
    onChangeDateValue(undefined)
    onChangeDateFrom(undefined)
    onChangeDateTo(undefined)
    setAmountFromDisplay("")
    setAmountToDisplay("")
  }

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.funding.title")}
      icon="hand-holding-dollar"
      tooltip={t("js.back.discover.filters.funding.tooltip")}
      count={activeFilterCount}
      hasValues={hasValues}
      onClear={handleClear}
      isLocked={isLocked}
      lockedTooltip={t("js.back.discover.filters.funding.locked_popover")}
    >
      {/* Series Selection */}
      <fieldset className="h-fieldset mb-6">
        <legend className="h-legend h-legend--sm">{t("js.back.discover.filters.funding.series_round")}</legend>
        <div className="h-form-group mb-2">
          <input
            type="checkbox"
            id="funding-series-any"
            className="h-checkbox"
            checked={allSeriesSelected}
            onChange={handleToggleAllSeries}
          />
          <label htmlFor="funding-series-any" className="h-label h-label--regular mb-0">
            {t("js.back.discover.filters.funding.any")}
          </label>
        </div>
        {VALID_SERIES.map((series) => (
          <div key={series.value} className="h-form-group mb-2">
            <input
              type="checkbox"
              id={`funding-series-${series.value}`}
              className="h-checkbox"
              checked={seriesValues.includes(series.value)}
              onChange={() => handleToggleSeries(series.value)}
            />
            <label
              htmlFor={`funding-series-${series.value}`}
              className="h-label h-label--regular mb-0"
            >
              {t(`js.back.discover.filters.funding.series.${series.key}`)}
            </label>
          </div>
        ))}
      </fieldset>

      {/* Amount Range */}
      <div className="mb-6">
        <fieldset className="h-fieldset">
          <legend className="h-legend h-legend--sm mb-0">{t("js.back.discover.filters.funding.series_amount")}</legend>
          <div className="signal-custom-dates">
            <div className="form-group">
              <label htmlFor="funding-amount-from" className="h-label h-label--regular">
                {t("js.back.discover.filters.from")}
              </label>
              <div className="h-input-group">
                <span className="h-input-group__text">
                  <span className="far fa-dollar" />
                </span>
                <input
                  type="text"
                  id="funding-amount-from"
                  className="h-input"
                  value={amountFromDisplay}
                  onChange={handleAmountFromChange}
                  onBlur={handleAmountFromBlur}
                  placeholder={t("js.back.discover.filters.funding.amount_from_placeholder")}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="funding-amount-to" className="h-label h-label--regular">
                {t("js.back.discover.filters.to")}
              </label>
              <div className="h-input-group">
                <span className="h-input-group__text">
                  <span className="far fa-dollar" />
                </span>
                <input
                  type="text"
                  id="funding-amount-to"
                  className="h-input"
                  value={amountToDisplay}
                  onChange={handleAmountToChange}
                  onBlur={handleAmountToBlur}
                  placeholder={t("js.back.discover.filters.funding.amount_to_placeholder")}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      {/* Date Filter */}
      <div>
        <label htmlFor="funding-date" className="h-label">
          {t("js.back.discover.filters.funding.date")}
        </label>
        <select
          id="funding-date"
          className="h-select h-select--block"
          value={dateValue || ""}
          onChange={handleDateChange}
        >
          <option value=""></option>
          {DATE_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`js.back.discover.filters.funding.date_options.${value}`)}
            </option>
          ))}
        </select>

        {dateValue === "custom" && (
          <div className="signal-custom-dates mt-2">
            <div className="form-group">
              <label htmlFor="funding-date-from" className="h-label h-label--regular">
                {t("js.back.discover.filters.from")}
              </label>
              <div className="h-input-group">
                <input
                  type="number"
                  id="funding-date-from"
                  className="h-input"
                  value={dateFrom ?? ""}
                  onChange={handleDateFromChange}
                  min="0"
                  max="365"
                  placeholder="0"
                />
                <span className="h-input-group__text">{t("js.back.discover.filters.funding.days_ago")}</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="funding-date-to" className="h-label h-label--regular">
                {t("js.back.discover.filters.to")}
              </label>
              <div className="h-input-group">
                <input
                  type="number"
                  id="funding-date-to"
                  className="h-input"
                  value={dateTo ?? ""}
                  onChange={handleDateToChange}
                  min="0"
                  max="365"
                  placeholder="365"
                />
                <span className="h-input-group__text">{t("js.back.discover.filters.funding.days_ago")}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsableFilter>
  )
}
